import React, { useState, useEffect, useRef } from "react"
import { library } from "@fortawesome/fontawesome-svg-core"
import { fas }     from "@fortawesome/free-solid-svg-icons"
library.add(fas)
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { getRequest, patchRequest, postRequest, formRequest, deleteRequest } from "@react/helpers/fetch"

// ── Bilingual label helper (FR primary, EN muted) ──────────────────────────
const Bi = ({ fr, en }) => (<span>{fr} <span className="font-normal text-slate-400">/ {en}</span></span>)

const STEPS = [
  { k: "mode",    label: "Mode" },
  { k: "upload",  label: "Upload" },
  { k: "analyse", label: "Analyse" },
  { k: "mapping", label: "Mapping" },
  { k: "preview", label: "Aperçu" },
  { k: "import",  label: "Import" },
  { k: "report",  label: "Rapport" },
]

const MODES = [
  { k: "guided", icon: "file-lines",   fr: "Import guidé",  en: "Guided import",   badge: "Recommandé",
    desc: "Choisissez l'objet, mappez les colonnes et validez chaque étape." },
  { k: "auto",   icon: "wand-magic-sparkles", fr: "Import automatique", en: "Automatic import", badge: "IA",
    desc: "Déposez un fichier : Yuman détecte l'objet et pré-mappe les colonnes." },
  { k: "chain",  icon: "diagram-project", fr: "Multi-fichiers enchaînés", en: "Multi-file chain", badge: "IA",
    desc: "Déposez plusieurs fichiers : détection, tri par dépendances et import en cascade." },
]

const STRATEGIES = [
  { k: "create", fr: "Créer uniquement",       en: "Create only" },
  { k: "both",   fr: "Créer et mettre à jour",  en: "Create & update" },
  { k: "update", fr: "Mettre à jour uniquement", en: "Update only" },
]

const BUSY_STATUSES = ["testing", "in_progress", "reverting"]

// Per-row staging buckets (what will happen on commit).
const STAGING = [
  { k: "ready_to_create", icon: "circle-plus",  fr: "À créer",           en: "To create", cls: "bg-emerald-50 border-emerald-200 text-emerald-700", dot: "bg-emerald-500" },
  { k: "ready_to_update", icon: "pen-to-square", fr: "À mettre à jour",   en: "To update", cls: "bg-blue-50 border-blue-200 text-blue-700",         dot: "bg-blue-500" },
  { k: "ambiguous",       icon: "circle-question", fr: "À vérifier",      en: "To review", cls: "bg-amber-50 border-amber-200 text-amber-700",       dot: "bg-amber-500" },
  { k: "rejected",        icon: "circle-xmark",  fr: "Rejetées",          en: "Rejected",  cls: "bg-rose-50 border-rose-200 text-rose-700",          dot: "bg-rose-500" },
]

const STATUS_STYLE = {
  done:        { cls: "bg-emerald-100 text-emerald-700", fr: "Terminé" },
  in_progress: { cls: "bg-blue-100 text-blue-700",       fr: "En cours" },
  testing:     { cls: "bg-amber-100 text-amber-700",     fr: "Test" },
  tested:      { cls: "bg-amber-100 text-amber-700",     fr: "Testé" },
  reverting:   { cls: "bg-slate-100 text-slate-600",     fr: "Annulation" },
  reverted:    { cls: "bg-slate-100 text-slate-500",     fr: "Annulé" },
  cancelled:   { cls: "bg-slate-100 text-slate-500",     fr: "Annulé" },
  created:     { cls: "bg-slate-100 text-slate-600",     fr: "Créé" },
}

const App = ({ catalog = [], stats = {}, urls = {} }) => {
  const [view, setView] = useState("wizard") // wizard | history | detail
  const [wizardKey, setWizardKey] = useState(0)
  const [detailId, setDetailId] = useState(null)

  const restart = () => { setWizardKey(k => k + 1); setView("wizard") }
  const openDetail = id => { setDetailId(id); setView("detail") }

  return (
    <div className="font-sans bg-slate-50 min-h-screen text-slate-900">
      <div className="max-w-[1400px] mx-auto px-8 py-8">
        <header className="flex items-start justify-between gap-6 mb-8">
          <div>
            <h1 className="text-3xl font-bold tracking-tight">Import Center</h1>
            <p className="text-slate-500 mt-1"><Bi fr="Importez vos données dans Yuman" en="Import your data into Yuman" /></p>
          </div>
          <div className="flex items-center gap-2">
            {view === "wizard" && (
              <button onClick={restart} className="inline-flex items-center gap-2 h-10 px-4 rounded-lg border border-slate-200 bg-white text-slate-600 text-sm font-medium hover:bg-slate-50">
                <FontAwesomeIcon icon="rotate-right" /> <Bi fr="Recommencer" en="Restart" />
              </button>
            )}
            <button onClick={() => setView(view === "wizard" ? "history" : "wizard")}
                    className="inline-flex items-center gap-2 h-10 px-4 rounded-lg border border-slate-200 bg-white text-slate-600 text-sm font-medium hover:bg-slate-50">
              <FontAwesomeIcon icon={view === "wizard" ? "clock-rotate-left" : "arrow-left"} />
              {view === "wizard" ? <Bi fr="Historique" en="History" /> : <Bi fr="Import Center" en="Import Center" />}
            </button>
          </div>
        </header>

        {view === "wizard"  && <Wizard key={wizardKey} catalog={catalog} stats={stats} urls={urls} onExit={restart} onSeeHistory={() => setView("history")} onOpenDetail={openDetail} />}
        {view === "history" && <History urls={urls} onOpen={openDetail} />}
        {view === "detail"  && <ImportDetail id={detailId} urls={urls} onBack={() => setView("history")} onChanged={() => setView("history")} />}
      </div>
    </div>
  )
}

// Small stats dashboard, shown on the landing (mode selection) screen.
const StatsDashboard = ({ stats = {} }) => (
  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
    {[["importable", stats.importable, "Objets importables", "Importable objects"],
      ["recent_30d", stats.recent_30d, "Imports (30 j)", "Imports (30d)"],
      ["done", stats.done, "Terminés", "Completed"],
      ["reusable", stats.reusable, "Modèles", "Templates"]].map(([k, v, fr, en]) => (
      <div key={k} className="bg-white border border-slate-200 rounded-xl p-4">
        <div className="text-2xl font-bold tabular-nums">{v ?? 0}</div>
        <div className="text-xs text-slate-500 mt-1"><Bi fr={fr} en={en} /></div>
      </div>
    ))}
  </div>
)

// ── STEPPER ──────────────────────────────────────────────────────────────
const Stepper = ({ current, onStepClick, maxReached }) => {
  const idx = STEPS.findIndex(s => s.k === current)
  return (
    <div className="flex items-center gap-1 mb-8 overflow-x-auto">
      {STEPS.map((s, i) => (
        <React.Fragment key={s.k}>
          <button
            disabled={i > maxReached}
            onClick={() => i <= maxReached && onStepClick(s.k)}
            className={`flex items-center gap-2 shrink-0 ${i > maxReached ? "cursor-default" : "cursor-pointer"}`}>
            <span className={`w-8 h-8 grid place-items-center rounded-full text-[13px] font-semibold border transition ${
              i < idx  ? "bg-brand-500 border-brand-500 text-white"
              : i === idx ? "bg-brand-600 border-brand-600 text-white"
              : "bg-white border-slate-300 text-slate-400"}`}>
              {i < idx ? <FontAwesomeIcon icon="check" /> : i + 1}
            </span>
            <span className={`text-[13px] font-medium ${i === idx ? "text-slate-900" : "text-slate-400"}`}>{s.label}</span>
          </button>
          {i < STEPS.length - 1 && <span className={`w-8 h-px shrink-0 ${i < idx ? "bg-brand-400" : "bg-slate-200"}`} />}
        </React.Fragment>
      ))}
    </div>
  )
}

// ── SIMULATION OVERLAY (live dry-run) ───────────────────────────────────────
// Live modal for the dry-run: real progress bar + counters (validated /
// rejected / warnings), a compact error preview, and a Stop button — fed by
// polling import_status while the runner validates each row.
const ProgressOverlay = ({ phase, live, onStop }) => {
  const [stopping, setStopping] = useState(false)
  const cfg = {
    test:   { icon: "flask",  fr: "Simulation en cours…", en: "Running simulation…", accent: "bg-amber-500",
              note: "Nous validons chaque ligne à blanc. Aucune donnée n'est modifiée." },
    revert: { icon: "rotate-left", fr: "Annulation…", en: "Reverting…", accent: "bg-slate-500",
              note: "Suppression des enregistrements créés par cet import." },
  }[phase] || { icon: "flask", fr: "Traitement…", en: "Processing…", accent: "bg-amber-500", note: "" }

  const status      = live && live.status
  const running     = !status || ["testing", "in_progress", "reverting"].includes(status)
  const pct         = (live && live.percentage) || 0
  const determinate = pct > 0
  const processed   = (live && live.processed) || 0
  const total       = (live && live.total) || 0
  const rejected    = (live && (live.rejected_count ?? live.error_count)) || 0
  const warnings    = (live && live.warning_count) || 0
  const errs        = ((live && live.errors) || []).slice(0, 4)
  const doStop      = () => { setStopping(true); onStop && onStop() }

  return (
    <div className="fixed inset-0 z-50 grid place-items-center bg-slate-900/40 backdrop-blur-sm p-4">
      <div className="w-[min(94vw,500px)] bg-white rounded-2xl shadow-xl border border-slate-200 p-7">
        <div className="text-center">
          <div className="w-14 h-14 mx-auto grid place-items-center rounded-full bg-amber-50 text-amber-600 text-xl mb-4">
            <FontAwesomeIcon icon={cfg.icon} />
          </div>
          <div className="font-semibold text-slate-900"><Bi fr={cfg.fr} en={cfg.en} /></div>
          {cfg.note && <p className="text-xs text-slate-500 mt-1.5 leading-snug">{cfg.note}</p>}
        </div>

        <div className="mt-5">
          <div className="flex justify-between text-xs text-slate-500 mb-1.5">
            <span className="tabular-nums">{determinate ? `${processed} / ${total} lignes validées` : "Préparation…"}</span>
            <span className="tabular-nums">{determinate ? `${Math.round(pct)} %` : ""}</span>
          </div>
          <div className="h-2 rounded-full bg-slate-200 overflow-hidden">
            {determinate
              ? <div className={`h-full ${cfg.accent} transition-all duration-300`} style={{ width: `${pct}%` }} />
              : <div className={`h-full w-1/3 ${cfg.accent} rounded-full`} style={{ animation: "ic-indeterminate 1.1s ease-in-out infinite" }} />}
          </div>
        </div>

        <div className="grid grid-cols-3 gap-2 mt-4">
          {[["Validées", processed, "text-emerald-600", "circle-check"],
            ["Rejetées", rejected, rejected ? "text-rose-600" : "text-slate-400", "circle-xmark"],
            ["Warnings", warnings, warnings ? "text-amber-600" : "text-slate-400", "triangle-exclamation"]].map(([l, v, c, ic]) => (
            <div key={l} className="rounded-lg border border-slate-200 p-2 text-center">
              <div className={`text-lg font-bold tabular-nums ${c}`}>{v}</div>
              <div className="text-[10px] text-slate-500"><FontAwesomeIcon icon={ic} className={c} /> {l}</div>
            </div>
          ))}
        </div>

        {errs.length > 0 && (
          <div className="mt-3 rounded-lg border border-rose-100 bg-rose-50/60 max-h-28 overflow-y-auto">
            <ul className="divide-y divide-rose-100">
              {errs.map((e, i) => (
                <li key={i} className="flex items-start gap-2 px-3 py-1.5 text-[12px]">
                  <span className="shrink-0 mt-0.5 font-mono text-[10px] text-rose-500">L.{e.row ?? "—"}</span>
                  <span className="text-rose-700 whitespace-pre-line">{e.message}</span>
                </li>
              ))}
              {rejected > errs.length && <li className="px-3 py-1 text-[11px] text-rose-400 italic">… {rejected - errs.length} autre(s)</li>}
            </ul>
          </div>
        )}

        <div className="flex items-center justify-between mt-5">
          <p className="text-xs text-slate-400">Aucune donnée modifiée / No data changed</p>
          {running && live && live.cancel_url && (
            <button onClick={doStop} disabled={stopping}
                    className="inline-flex items-center gap-2 h-9 px-4 rounded-lg border border-rose-200 bg-white text-rose-600 text-sm font-medium hover:bg-rose-50 disabled:opacity-50">
              <FontAwesomeIcon icon={stopping ? "spinner" : "stop"} spin={stopping} /> {stopping ? "Arrêt…" : "Arrêter"}
            </button>
          )}
        </div>
      </div>
      <style>{`@keyframes ic-indeterminate{0%{margin-left:-33%}100%{margin-left:100%}}`}</style>
    </div>
  )
}

// ── CONTROL TOWER helpers ───────────────────────────────────────────────────
const fmtRate = r => !r || r < 0.1 ? "—" : r >= 100 ? `${Math.round(r)}/s` : `${r.toFixed(1)}/s`
const fmtEta = s => {
  if (!isFinite(s) || s <= 0) return "—"
  if (s < 60) return `~${Math.ceil(s)} s`
  const m = Math.floor(s / 60)
  return s < 3600 ? `~${m} min ${Math.round(s % 60)} s` : `~${Math.floor(s / 3600)} h ${Math.round((s % 3600) / 60)} min`
}

// Live counter grid — the heart of the control tower.
const CounterTiles = ({ live, dense }) => {
  const tiles = [
    { fr: "Créés",       en: "Created", v: live.created_count,  cls: "text-emerald-600", icon: "circle-plus" },
    { fr: "Mis à jour",  en: "Updated", v: live.updated_count,  cls: "text-blue-600",    icon: "pen-to-square" },
    { fr: "Rejetés",     en: "Rejected", v: live.rejected_count ?? live.error_count, cls: "text-rose-600", icon: "circle-xmark" },
    { fr: "Warnings",    en: "Warnings", v: live.warning_count, cls: "text-amber-600",   icon: "triangle-exclamation" },
  ]
  return (
    <div className={`grid grid-cols-2 ${dense ? "md:grid-cols-4 gap-2" : "md:grid-cols-4 gap-3"}`}>
      {tiles.map(t => (
        <div key={t.fr} className={`bg-white border border-slate-200 rounded-xl ${dense ? "p-2.5" : "p-4"}`}>
          <div className="flex items-center gap-1.5 text-[11px] font-medium text-slate-500"><FontAwesomeIcon icon={t.icon} className={t.cls} /> {t.fr}</div>
          <div className={`font-bold tabular-nums ${dense ? "text-lg" : "text-2xl"} ${t.cls}`}>{t.v ?? 0}</div>
        </div>
      ))}
    </div>
  )
}

// Errors + warnings journal, two filterable streams — clear and designed.
const LogConsole = ({ errors = [], warnings = [], errorTotal, warningTotal, compact }) => {
  const [tab, setTab] = useState("errors")
  const [q, setQ]     = useState("")
  const active = tab === "errors" ? errors : warnings
  const filtered = active.filter(l => !q || `${l.row} ${l.message}`.toLowerCase().includes(q.toLowerCase()))
  const total = tab === "errors" ? (errorTotal ?? errors.length) : (warningTotal ?? warnings.length)
  const capped = total > active.length
  return (
    <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden">
      <div className="flex items-center gap-2 px-4 pt-3">
        {[["errors", "Erreurs", errorTotal ?? errors.length, "circle-xmark", "bg-rose-50 text-rose-700 ring-1 ring-rose-200", "bg-rose-100 text-rose-700"],
          ["warnings", "Warnings", warningTotal ?? warnings.length, "triangle-exclamation", "bg-amber-50 text-amber-700 ring-1 ring-amber-200", "bg-amber-100 text-amber-700"]].map(([k, label, n, icon, activeCls, badgeCls]) => (
          <button key={k} onClick={() => setTab(k)}
                  className={`inline-flex items-center gap-2 px-3 h-9 rounded-lg text-sm font-medium transition ${tab === k ? activeCls : "text-slate-500 hover:bg-slate-50"}`}>
            <FontAwesomeIcon icon={icon} />
            {label}
            <span className={`tabular-nums text-[11px] px-1.5 py-0.5 rounded-full ${n > 0 ? badgeCls : "bg-slate-100 text-slate-500"}`}>{n}</span>
          </button>
        ))}
        <div className="relative ml-auto w-48">
          <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400 text-xs"><FontAwesomeIcon icon="magnifying-glass" /></span>
          <input value={q} onChange={e => setQ(e.target.value)} placeholder="Filtrer…" className="w-full h-8 pl-7 pr-2 rounded-md border border-slate-200 text-xs focus:outline-none focus:ring-2 focus:ring-brand-200" />
        </div>
      </div>
      <div className={`mt-2 border-t border-slate-100 overflow-y-auto ${compact ? "max-h-48" : "max-h-80"}`}>
        {filtered.length === 0 ? (
          <div className="py-10 text-center text-slate-400 text-sm">
            <FontAwesomeIcon icon={tab === "errors" ? "circle-check" : "inbox"} className="text-xl mb-2 block" />
            {tab === "errors" ? "Aucune erreur" : "Aucun warning"}
          </div>
        ) : (
          <ul className="divide-y divide-slate-100">
            {filtered.map((l, i) => (
              <li key={i} className="flex items-start gap-3 px-4 py-2 text-[13px]">
                <span className={`shrink-0 mt-0.5 tabular-nums text-[11px] font-mono px-1.5 py-0.5 rounded ${tab === "errors" ? "bg-rose-50 text-rose-600" : "bg-amber-50 text-amber-700"}`}>L.{l.row ?? "—"}</span>
                <span className="text-slate-700 whitespace-pre-line">{l.message}</span>
              </li>
            ))}
            {capped && <li className="px-4 py-2 text-xs text-slate-400 italic">… {total - active.length} de plus non affichées (plafond {active.length}).</li>}
          </ul>
        )}
      </div>
    </div>
  )
}

// The import step: a real-time control console (progress, counters, throughput,
// stop, live error/warning journal).
const ImportConsole = ({ live, target, onStop }) => {
  const [stopping, setStopping] = useState(false)
  if (!live) return (
    <div className="bg-white border border-slate-200 rounded-2xl p-16 text-center text-slate-500">
      <FontAwesomeIcon icon="spinner" spin className="text-2xl mb-3" /><div>Démarrage…</div>
    </div>
  )
  const running = ["in_progress", "testing", "reverting"].includes(live.status)
  const cancelled = live.status === "cancelled"
  const pct = live.percentage || 0
  const total = live.total || 0, processed = live.processed || 0
  const remaining = Math.max(total - processed, 0)
  const rate = live._rate || 0
  const eta = running && rate > 0.1 ? fmtEta(remaining / rate) : "—"
  const doStop = () => { setStopping(true); onStop() }

  return (
    <div className="space-y-4">
      <div className="bg-white border border-slate-200 rounded-2xl p-6">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="flex items-center gap-3">
            <span className={`w-11 h-11 grid place-items-center rounded-xl text-lg ${cancelled ? "bg-slate-100 text-slate-500" : running ? "bg-brand-50 text-brand-600" : "bg-emerald-50 text-emerald-600"}`}>
              <FontAwesomeIcon icon={cancelled ? "ban" : running ? "rocket" : "circle-check"} spin={running && rate === 0} />
            </span>
            <div>
              <div className="font-semibold text-slate-900">
                {cancelled ? <Bi fr="Import arrêté" en="Import stopped" /> : running ? <Bi fr="Import en cours" en="Import running" /> : <Bi fr="Import terminé" en="Import complete" />}
              </div>
              <div className="text-xs text-slate-500">{target?.name || live.type} · {live.mode}</div>
            </div>
          </div>
          {running && (
            <button onClick={doStop} disabled={stopping || !live.cancellable}
                    className="inline-flex items-center gap-2 h-10 px-4 rounded-lg border border-rose-200 bg-white text-rose-600 text-sm font-medium hover:bg-rose-50 disabled:opacity-50">
              <FontAwesomeIcon icon={stopping ? "spinner" : "stop"} spin={stopping} /> {stopping ? "Arrêt…" : "Arrêter"}
            </button>
          )}
        </div>

        <div className="mt-5">
          <div className="flex justify-between text-xs text-slate-500 mb-1.5">
            <span className="tabular-nums">{processed} / {total} lignes traitées · reste {remaining}</span>
            <span className="tabular-nums">{Math.round(pct)} %</span>
          </div>
          <div className="h-2.5 rounded-full bg-slate-200 overflow-hidden">
            <div className={`h-full transition-all duration-300 ${cancelled ? "bg-slate-400" : running ? "bg-brand-500" : "bg-emerald-500"}`} style={{ width: `${pct}%` }} />
          </div>
          <div className="flex gap-5 mt-2 text-xs text-slate-400">
            <span><FontAwesomeIcon icon="gauge-high" /> Débit {fmtRate(rate)}</span>
            <span><FontAwesomeIcon icon="hourglass-half" /> Restant {eta}</span>
          </div>
        </div>

        <div className="mt-5"><CounterTiles live={live} /></div>
      </div>

      <LogConsole errors={live.errors} warnings={live.warnings} errorTotal={live.rejected_count ?? live.error_count} warningTotal={live.warning_count} />
    </div>
  )
}

// ── WIZARD ─────────────────────────────────────────────────────────────────
const Wizard = ({ catalog, stats, urls, onExit, onSeeHistory, onOpenDetail }) => {
  const [step, setStep]       = useState("mode")
  const [maxReached, setMax]  = useState(0)
  const [mode, setMode]       = useState(null)
  const [target, setTarget]   = useState(null)   // catalog object
  const [strategy, setStrat]  = useState("both")
  const [busy, setBusy]       = useState(false)
  const [phase, setPhase]     = useState(null)  // test | commit | revert (drives overlay)
  const [error, setError]     = useState(null)
  const [progress, setProg]   = useState(0)

  const [lastUpload, setLast] = useState(null)   // { file, text, limit } — for re-detection
  const [data, setData]       = useState(null)   // create payload
  const [detection, setDetect]= useState(null)   // ranked candidates (auto mode)
  const [mapping, setMapping] = useState({})
  const [test, setTest]       = useState(null)
  const [result, setResult]   = useState(null)
  const [live, setLive]       = useState(null)   // live commit payload (counters)
  const rateRef = useRef({ t: 0, processed: 0, rate: 0 })
  const aliveRef = useRef(true)
  const pollTimerRef = useRef(null)
  useEffect(() => () => { aliveRef.current = false; clearTimeout(pollTimerRef.current) }, [])

  const byKey = key => catalog.find(o => o.key === key) || null
  const importUrl = id => `${urls.base}/${id}`
  const goto = k => { setStep(k); setMax(m => Math.max(m, STEPS.findIndex(s => s.k === k))) }

  const poll = (id, done) => getRequest(importUrl(id)).then(p => {
    setProg(p.percentage || 0)
    BUSY_STATUSES.includes(p.status) ? setTimeout(() => poll(id, done), 1000) : done(p)
  })

  // Live polling for the (async) real import — feeds the control console with
  // counters and a smoothed throughput (rows/s) computed from two snapshots.
  // Stops on unmount (restart remounts via key) and caps error retries.
  const pollLive = (id, onDone, tries = 0) => {
    if (!aliveRef.current) return
    getRequest(importUrl(id)).then(p => {
      if (!aliveRef.current) return
      const now = Date.now(), prev = rateRef.current, dt = (now - prev.t) / 1000
      if (prev.t && dt > 0.05 && (p.processed || 0) >= prev.processed) {
        const inst = ((p.processed || 0) - prev.processed) / dt
        p._rate = prev.rate ? prev.rate * 0.6 + inst * 0.4 : inst
      } else { p._rate = prev.rate || 0 }
      rateRef.current = { t: now, processed: p.processed || 0, rate: p._rate || 0 }
      setLive(p); setProg(p.percentage || 0)
      if (BUSY_STATUSES.includes(p.status)) pollTimerRef.current = setTimeout(() => pollLive(id, onDone, 0), 750)
      else onDone(p)
    }).catch(() => {
      if (!aliveRef.current || tries >= 8) return
      pollTimerRef.current = setTimeout(() => pollLive(id, onDone, tries + 1), 1500)
    })
  }

  const stopImport = () => { if (live?.cancel_url) postRequest({ url: live.cancel_url }).catch(() => {}) }

  const chooseMode = m => { setMode(m); if (m === "auto") setTarget(null); goto("upload") }

  // type omitted/"auto" → the backend auto-detects. In guided/flat modes we
  // pass the chosen object; in auto mode we let the file speak for itself.
  const submitUpload = ({ file, text, limit }) => {
    if (mode !== "auto" && !target) { setError("Sélectionnez d'abord un objet à importer."); return }
    if (!file && !text) return
    setLast({ file, text, limit })
    ingest({ file, text, limit, type: mode === "auto" ? "auto" : target.key })
  }

  const ingest = ({ file, text, limit, type }) => {
    setBusy(true); setError(null)
    const form = new FormData()
    form.append("type_of_import", type || "auto")
    form.append("mode", strategy)
    if (limit) form.append("limit", limit)
    if (file) form.append("data_file", file)
    if (text) form.append("csv_text", text)
    formRequest({ url: urls.create, form })
      .then(payload => {
        if (payload.error) {
          setError(payload.error)
          if (payload.detection) setDetect(payload.detection) // couldn't pick — let user choose
          return
        }
        setData(payload)
        setDetect(payload.detection || null)
        if (payload.type) setTarget(byKey(payload.type) || { key: payload.type, name: payload.type })
        setMapping(payload.suggestion || {})
        goto("analyse")
      })
      .catch(() => setError("Erreur réseau — réessayer."))
      .finally(() => setBusy(false))
  }

  // Override the detected object: re-ingest the same file as another type.
  const changeType = typeKey => {
    if (!lastUpload) return
    setTarget(byKey(typeKey) || { key: typeKey, name: typeKey })
    ingest({ ...lastUpload, type: typeKey })
  }

  // Simulation reached a terminal state → fetch the detailed preview (staging,
  // enriched errors, duplicates) and move on. If it was stopped, return to
  // mapping instead.
  const finishTest = p => {
    setBusy(false)
    if (p.status === "cancelled") { setPhase(null); setLive(null); setStep("mapping"); setError("Simulation arrêtée."); return }
    getRequest(`${importUrl(data.id)}/preview`)
      .then(prev => { setTest({ ...p, ...prev }); goto("preview") })
      .catch(() => { setTest(p); goto("preview") })
      .finally(() => { setPhase(null); setLive(null) })
  }

  const runTest = () => {
    setBusy(true); setError(null); setProg(0); setPhase("test"); setLive(null)
    rateRef.current = { t: 0, processed: 0, rate: 0 }
    const columns = {}
    Object.entries(mapping).forEach(([h, f]) => { if (f) columns[h] = f })
    patchRequest({ url: importUrl(data.id), body: { columns, mode: strategy } })
      .then(payload => {
        if (payload.valid === false) { setError((payload.errors || []).join(" · ")); setBusy(false); setPhase(null); return }
        setLive(payload)
        BUSY_STATUSES.includes(payload.status) ? pollLive(payload.id, finishTest) : finishTest(payload)
      })
      .catch(() => { setError("Erreur réseau — réessayer."); setBusy(false); setPhase(null) })
  }

  const runCorrect = corrections => {
    setBusy(true); setError(null); setPhase("test"); setLive(null)
    rateRef.current = { t: 0, processed: 0, rate: 0 }
    patchRequest({ url: `${importUrl(data.id)}/correct`, body: { corrections } })
      .then(payload => {
        if (payload.valid === false) { setError((payload.errors || []).join(" · ")); setBusy(false); setPhase(null); return }
        setLive(payload)
        BUSY_STATUSES.includes(payload.status) ? pollLive(payload.id, finishTest) : finishTest(payload)
      })
      .catch(() => { setError("Erreur réseau — réessayer."); setBusy(false); setPhase(null) })
  }

  const launch = () => {
    setError(null); setProg(0)
    rateRef.current = { t: 0, processed: 0, rate: 0 }
    goto("import")
    postRequest({ url: `${importUrl(data.id)}/commit` })
      .then(payload => {
        if (payload.error) { setError(payload.error); setStep("preview"); return }
        setLive(payload)
        const finish = p => { setResult(p); setLive(null); goto("report") }
        BUSY_STATUSES.includes(payload.status) ? pollLive(payload.id, finish) : finish(payload)
      })
      .catch(() => { setError("Erreur réseau — réessayer."); setStep("preview") })
  }

  if (mode === "chain") return <MultiChain catalog={catalog} urls={urls} strategy={strategy} setStrategy={setStrat} onExit={onExit} onSeeHistory={onSeeHistory} />

  return (
    <div>
      <Stepper current={step} maxReached={maxReached} onStepClick={goto} />

      {phase === "test" && <ProgressOverlay phase="test" live={live} onStop={stopImport} />}

      {error && (
        <div className="mb-5 flex items-start gap-2 rounded-lg border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
          <FontAwesomeIcon icon="triangle-exclamation" className="mt-0.5" /><span>{error}</span>
        </div>
      )}

      {step === "mode"    && <ModeStep stats={stats} onChoose={chooseMode} />}
      {step === "upload"  && <UploadStep catalog={catalog} target={target} setTarget={setTarget} strategy={strategy} setStrategy={setStrat} urls={urls} busy={busy} mode={mode} onSubmit={submitUpload} onBack={() => setStep("mode")} />}
      {step === "analyse" && data && <AnalyseStep data={data} detection={detection} target={target} catalog={catalog} onChangeType={changeType} onBack={() => setStep("upload")} onNext={() => goto("mapping")} />}
      {step === "mapping" && data && <MappingStep data={data} mapping={mapping} setMapping={setMapping} strategy={strategy} setStrategy={setStrat} busy={busy} onBack={() => setStep("analyse")} onTest={runTest} />}
      {step === "preview" && test && <PreviewStep test={test} schema={data.schema} mapping={mapping} busy={busy} onBack={() => setStep("mapping")} onCorrect={runCorrect} onLaunch={launch} />}
      {step === "import"  && <ImportConsole live={live} target={target} onStop={stopImport} />}
      {step === "report"  && result && <ReportStep result={result} target={target} urls={urls} onNew={onExit} onHistory={onSeeHistory} onOpenDetail={onOpenDetail} />}
    </div>
  )
}

// ── STEP: Mode (landing) ────────────────────────────────────────────────────
const ModeStep = ({ stats, onChoose }) => (
  <div className="space-y-8">
    <div>
      <h2 className="text-xl font-semibold mb-1"><Bi fr="Choisissez votre mode d'import" en="Choose your import mode" /></h2>
      <p className="text-slate-500 mb-6"><Bi fr="Sélectionnez le niveau d'automatisation souhaité" en="Select the desired automation level" /></p>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {MODES.map(m => (
          <button key={m.k} onClick={() => onChoose(m.k)} className="group text-left bg-white border border-slate-200 rounded-2xl p-6 hover:border-brand-400 hover:shadow-md transition">
            <div className="flex items-start justify-between">
              <span className="w-12 h-12 grid place-items-center rounded-xl bg-brand-50 text-brand-600 text-lg group-hover:bg-brand-500 group-hover:text-white transition"><FontAwesomeIcon icon={m.icon} /></span>
              <span className={`text-[11px] font-semibold px-2.5 py-1 rounded-full ${m.badge === "IA" ? "bg-violet-100 text-violet-700" : m.badge === "Avancé" ? "bg-slate-100 text-slate-600" : "bg-brand-100 text-brand-700"}`}>{m.badge}</span>
            </div>
            <h3 className="text-lg font-semibold mt-4">{m.fr}</h3>
            <div className="text-xs text-slate-400">{m.en}</div>
            <p className="text-sm text-slate-500 mt-2 leading-snug">{m.desc}</p>
          </button>
        ))}
      </div>
    </div>
    <StatsDashboard stats={stats} />
  </div>
)

// ── STEP: Upload (target + strategy + file/paste) ───────────────────────────
const UploadStep = ({ catalog, target, setTarget, strategy, setStrategy, urls, busy, mode, onSubmit, onBack }) => {
  const inputRef = useRef(null)
  const [drag, setDrag] = useState(false)
  const [tab, setTab]   = useState("file")
  const [text, setText] = useState("")
  const [q, setQ]       = useState("")
  const [limitOn, setLimitOn] = useState(false)
  const [limit, setLimit]     = useState(100)
  const auto = mode === "auto"
  const list = catalog.filter(o => !q || o.name.toLowerCase().includes(q.toLowerCase()))
  const limitVal = limitOn && Number(limit) > 0 ? Number(limit) : null
  const send = extra => onSubmit({ ...extra, limit: limitVal })

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
      {/* Target picker — in auto mode the object is detected from the file */}
      <div className="bg-white border border-slate-200 rounded-2xl p-5">
        <div className="text-sm font-semibold mb-1">
          {auto ? <Bi fr="Objet cible (auto)" en="Target object (auto)" /> : <Bi fr="Objet cible" en="Target object" />}
        </div>
        {auto ? (
          <div className="mt-2 rounded-xl bg-violet-50 border border-violet-200 p-3 text-[13px] text-violet-800">
            <div className="font-medium mb-1"><FontAwesomeIcon icon="wand-magic-sparkles" /> Détection automatique</div>
            <p className="leading-snug">Déposez votre fichier : Yuman identifie l'objet à partir des colonnes. Vous pourrez confirmer ou changer à l'étape suivante.</p>
            {target && <div className="mt-2 text-xs">Objet pré-sélectionné : <b>{target.name}</b> (modifiable)</div>}
          </div>
        ) : (
          <>
            <p className="text-xs text-slate-500 mb-3">Classé par importance</p>
            <div className="relative mb-2">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-xs"><FontAwesomeIcon icon="magnifying-glass" /></span>
              <input value={q} onChange={e => setQ(e.target.value)} placeholder="Rechercher…" className="w-full h-9 pl-8 pr-2 rounded-md border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-brand-200" />
            </div>
            <div className="max-h-72 overflow-y-auto -mx-1 px-1 space-y-1">
              {list.map(o => (
                <button key={o.key} onClick={() => setTarget(o)}
                        className={`w-full flex items-center gap-2 px-2.5 h-10 rounded-lg text-left text-sm transition ${target?.key === o.key ? "bg-brand-50 text-brand-700 ring-1 ring-brand-300" : "hover:bg-slate-50 text-slate-700"}`}>
                  <FontAwesomeIcon icon={o.icon} className="text-slate-400 w-4" />
                  <span className="flex-1 truncate">{o.name}</span>
                  {target?.key === o.key && <FontAwesomeIcon icon="check" className="text-brand-500" />}
                </button>
              ))}
            </div>
          </>
        )}
      </div>

      {/* Upload zone */}
      <div className="lg:col-span-2 space-y-4">
        <div className="bg-white border border-slate-200 rounded-2xl p-5">
          <div className="flex items-center justify-between gap-3 mb-4">
            <div className="text-sm font-semibold"><Bi fr="Stratégie d'import" en="Import strategy" /></div>
            <select value={strategy} onChange={e => setStrategy(e.target.value)} className="h-9 px-2 rounded-md border border-slate-200 text-sm bg-white">
              {STRATEGIES.map(s => <option key={s.k} value={s.k}>{s.fr}</option>)}
            </select>
          </div>

          {/* Record limit — import only the first N rows (safe trial run) */}
          <div className="flex items-center gap-3 mb-4 rounded-lg bg-slate-50 border border-slate-200 px-3 py-2">
            <label className="flex items-center gap-2 text-[13px] text-slate-700 cursor-pointer select-none">
              <input type="checkbox" checked={limitOn} onChange={e => setLimitOn(e.target.checked)} className="rounded border-slate-300 text-brand-500 focus:ring-brand-200" />
              <FontAwesomeIcon icon="filter" className="text-slate-400" /> <Bi fr="Limiter le nombre d'enregistrements" en="Limit record count" />
            </label>
            {limitOn && (
              <div className="flex items-center gap-1.5 text-sm">
                <input type="number" min="1" value={limit} onChange={e => setLimit(e.target.value)} className="w-20 h-8 px-2 rounded-md border border-slate-200 text-sm tabular-nums focus:outline-none focus:ring-2 focus:ring-brand-200" />
                <span className="text-slate-500">premières lignes</span>
              </div>
            )}
          </div>

          <div className="flex gap-1 mb-4 bg-slate-100 rounded-lg p-1 w-fit">
            {["file", "paste"].map(k => (
              <button key={k} onClick={() => setTab(k)} className={`px-3.5 h-8 rounded-md text-[13px] font-medium transition ${tab === k ? "bg-white text-slate-900 shadow-sm" : "text-slate-500"}`}>
                <FontAwesomeIcon icon={k === "file" ? "file-arrow-up" : "clipboard"} /> {k === "file" ? "Fichier" : "Coller"}
              </button>
            ))}
          </div>

          {tab === "file" ? (
            <div onClick={() => inputRef.current?.click()}
                 onDragOver={e => { e.preventDefault(); setDrag(true) }} onDragLeave={() => setDrag(false)}
                 onDrop={e => { e.preventDefault(); setDrag(false); send({ file: e.dataTransfer.files[0] }) }}
                 className={`cursor-pointer rounded-xl border-2 border-dashed px-6 py-14 text-center transition ${drag ? "border-brand-400 bg-brand-50" : "border-slate-300 hover:border-slate-400 bg-slate-50"}`}>
              <div className="text-3xl text-slate-400 mb-3"><FontAwesomeIcon icon={busy ? "spinner" : "cloud-arrow-up"} spin={busy} /></div>
              <div className="font-medium text-slate-800">{busy ? "Analyse…" : <Bi fr="Déposez votre fichier CSV ou Excel" en="Drop your CSV or Excel file" />}</div>
              <div className="text-xs text-slate-400 mt-2 font-mono">CSV / Excel (.xlsx) · séparateur & encodage auto-détectés</div>
              <input ref={inputRef} type="file" accept=".csv,.xlsx,.xlsm,text/csv" className="hidden" onChange={e => send({ file: e.target.files[0] })} />
            </div>
          ) : (
            <div>
              <textarea value={text} onChange={e => setText(e.target.value)} rows="8" placeholder="Collez vos données depuis Excel (1ère ligne = en-têtes)…"
                        className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-brand-200" />
              <div className="flex justify-end mt-3">
                <button onClick={() => send({ text })} disabled={busy || !text.trim()} className="inline-flex items-center gap-1.5 h-9 px-4 rounded-md bg-brand-500 hover:bg-brand-600 disabled:opacity-50 text-white text-sm font-medium">
                  <FontAwesomeIcon icon={busy ? "spinner" : "table-cells"} spin={busy} /> Analyser
                </button>
              </div>
            </div>
          )}
          {!auto && target && (
            <div className="mt-4 text-center">
              <a href={`${urls.sample}?table_name=${encodeURIComponent(target.key || "")}`} className="text-sm text-brand-600 hover:text-brand-700 font-medium"><FontAwesomeIcon icon="download" /> Télécharger le modèle « {target.name} »</a>
            </div>
          )}
        </div>
        <button onClick={onBack} className="text-sm text-slate-500 hover:text-slate-700"><FontAwesomeIcon icon="arrow-left" /> Retour</button>
      </div>
    </div>
  )
}

// ── STEP: Analyse ────────────────────────────────────────────────────────
const AnalyseStep = ({ data, detection, target, catalog, onChangeType, onBack, onNext }) => {
  const a = data.analysis || {}
  const cells = [
    ["Encodage", a.encoding || "—", "file-code"],
    ["Séparateur", a.delimiter || "—", "table-columns"],
    ["Colonnes", (data.headers || []).length, "table-list"],
    ["Lignes", a.row_count ?? "—", "list-ol"],
  ]
  const top = (detection && detection[0]) || null
  const alts = (detection || []).slice(1, 4)
  return (
    <div className="space-y-4">
      {/* Auto-detection result — confirm or override */}
      {data.auto && top && (
        <div className="rounded-2xl border border-violet-200 bg-violet-50 p-5">
          <div className="flex items-center justify-between gap-4 flex-wrap">
            <div>
              <div className="text-[13px] text-violet-700 font-medium mb-1"><FontAwesomeIcon icon="wand-magic-sparkles" /> Objet détecté automatiquement</div>
              <div className="text-lg font-semibold text-slate-900">
                {target?.name || top.name}
                <span className={`ml-2 align-middle text-[11px] font-semibold px-2 py-0.5 rounded-full ${top.confidence >= 75 ? "bg-emerald-100 text-emerald-700" : top.confidence >= 50 ? "bg-amber-100 text-amber-700" : "bg-rose-100 text-rose-700"}`}>
                  {top.confidence}% de confiance
                </span>
              </div>
              <div className="text-xs text-slate-500 mt-1">{top.mapped} colonnes reconnues · {top.required_hit}/{top.required_total} champs requis couverts</div>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-xs text-slate-500">Pas le bon objet ?</span>
              <select value={target?.key || top.type} onChange={e => onChangeType(e.target.value)}
                      className="h-9 px-2 rounded-md border border-violet-300 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-violet-200">
                {(catalog || []).map(o => <option key={o.key} value={o.key}>{o.name}</option>)}
              </select>
            </div>
          </div>
          {alts.length > 0 && (
            <div className="flex items-center gap-2 mt-3 pt-3 border-t border-violet-200 flex-wrap">
              <span className="text-xs text-violet-700">Autres possibilités :</span>
              {alts.map(c => (
                <button key={c.type} onClick={() => onChangeType(c.type)}
                        className="text-xs px-2.5 py-1 rounded-full bg-white border border-violet-200 text-slate-600 hover:border-violet-400">
                  {c.name} <span className="text-slate-400 tabular-nums">{c.confidence}%</span>
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {cells.map(([label, value, icon]) => (
          <div key={label} className="bg-white border border-slate-200 rounded-xl p-4">
            <div className="text-slate-400 text-sm"><FontAwesomeIcon icon={icon} /> {label}</div>
            <div className="text-lg font-semibold tabular-nums mt-1 truncate">{value}</div>
          </div>
        ))}
      </div>

      {/* On-the-fly cleaning & limit notices */}
      {(a.cleaned > 0 || a.limited) && (
        <div className="flex flex-wrap gap-2">
          {a.cleaned > 0 && (
            <span className="inline-flex items-center gap-1.5 text-[13px] rounded-lg bg-emerald-50 border border-emerald-200 text-emerald-700 px-3 py-1.5">
              <FontAwesomeIcon icon="broom" /> {a.cleaned} cellule(s) nettoyée(s) — espaces & caractères de contrôle
            </span>
          )}
          {a.limited && (
            <span className="inline-flex items-center gap-1.5 text-[13px] rounded-lg bg-amber-50 border border-amber-200 text-amber-700 px-3 py-1.5">
              <FontAwesomeIcon icon="filter" /> Limité aux {a.limited} premières lignes
            </span>
          )}
        </div>
      )}
      <div className="bg-white border border-slate-200 rounded-2xl p-5">
        <div className="text-sm font-semibold mb-3"><Bi fr="Aperçu du fichier" en="File preview" /></div>
        <div className="overflow-x-auto border border-slate-200 rounded-lg">
          <table className="text-[13px] w-full">
            <thead><tr className="bg-slate-50 text-slate-500 text-[11px] uppercase tracking-wider">
              {(data.headers || []).map(h => <th key={h} className="text-left font-medium px-3 py-2 whitespace-nowrap">{h}</th>)}
            </tr></thead>
            <tbody className="divide-y divide-slate-100">
              {Array.from({ length: Math.min(5, (data.sample?.[data.headers?.[0]] || []).length) }).map((_, r) => (
                <tr key={r}>{(data.headers || []).map(h => <td key={h} className="px-3 py-1.5 text-slate-600 whitespace-nowrap">{(data.sample?.[h] || [])[r] || ""}</td>)}</tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      <div className="flex items-center justify-between">
        <button onClick={onBack} className="text-sm text-slate-500 hover:text-slate-700"><FontAwesomeIcon icon="arrow-left" /> Retour</button>
        <button onClick={onNext} className="inline-flex items-center gap-1.5 h-10 px-4 rounded-lg bg-brand-500 hover:bg-brand-600 text-white text-sm font-medium">Continuer <FontAwesomeIcon icon="arrow-right" /></button>
      </div>
    </div>
  )
}

// ── STEP: Mapping (target-first + quality) ─────────────────────────────────
const MappingStep = ({ data, mapping, setMapping, strategy, setStrategy, busy, onBack, onTest }) => {
  // reverse: field -> header currently mapped
  const fieldToHeader = {}
  Object.entries(mapping).forEach(([h, f]) => { if (f) fieldToHeader[f] = h })
  const setFieldHeader = (fieldKey, header) => {
    const next = { ...mapping }
    Object.keys(next).forEach(h => { if (next[h] === fieldKey) next[h] = null })
    if (header) next[header] = fieldKey
    setMapping(next)
  }
  const missingReq = data.schema.filter(f => f.required && !fieldToHeader[f.key])

  return (
    <div className="space-y-4">
      <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden">
        <div className="px-5 py-4 border-b border-slate-200">
          <h2 className="font-semibold">Mapping des champs</h2>
          <p className="text-sm text-slate-500">Pour chaque champ cible Yuman, sélectionnez la colonne source du fichier.</p>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-[13px]">
            <thead><tr className="text-slate-500 text-[11px] uppercase tracking-wider border-b border-slate-200">
              <th className="text-left font-medium px-5 py-3"><Bi fr="Champ cible Yuman" en="Yuman field" /></th>
              <th className="text-left font-medium px-5 py-3 w-72"><Bi fr="Colonne source" en="Source column" /></th>
              <th className="text-left font-medium px-5 py-3"><Bi fr="Aperçu valeurs" en="Values preview" /></th>
              <th className="text-right font-medium px-5 py-3 w-32">Qualité</th>
            </tr></thead>
            <tbody className="divide-y divide-slate-100">
              {data.schema.map(f => {
                const header = fieldToHeader[f.key]
                const preview = header ? (data.sample?.[header] || []).slice(0, 3) : []
                const unmappedRequired = f.required && !header
                return (
                  <tr key={f.key} className={unmappedRequired ? "bg-rose-50" : ""}>
                    <td className="px-5 py-3">
                      <div className="font-medium text-slate-900 flex items-center gap-2">
                        {f.label}
                        {f.required && <span className="text-[10px] font-semibold bg-rose-600 text-white px-1.5 py-0.5 rounded">obligatoire</span>}
                        {f.key_field && <span className="text-[10px] font-mono text-amber-700 bg-amber-50 px-1.5 py-0.5 rounded" title={f.hint}>clé</span>}
                      </div>
                      <div className="text-xs text-slate-400 mt-0.5 font-mono">{f.type}</div>
                    </td>
                    <td className="px-5 py-3">
                      <select value={header || ""} onChange={e => setFieldHeader(f.key, e.target.value)}
                              className="w-full h-9 px-2 rounded-md border border-slate-200 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-brand-200">
                        <option value="">— Non mappé —</option>
                        {data.headers.map(h => <option key={h} value={h}>{h}</option>)}
                      </select>
                    </td>
                    <td className="px-5 py-3 text-slate-500 font-mono text-xs whitespace-pre-line">{preview.join("\n") || "—"}</td>
                    <td className="px-5 py-3 text-right">
                      {header
                        ? <span className="text-emerald-600 font-medium"><FontAwesomeIcon icon="circle-check" /> OK</span>
                        : unmappedRequired
                          ? <span className="text-rose-600 font-medium"><FontAwesomeIcon icon="circle-exclamation" /> Requis</span>
                          : <span className="text-slate-400"><FontAwesomeIcon icon="xmark" /> Non mappé</span>}
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      </div>

      {missingReq.length > 0 && (
        <div className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-4 py-2">
          <FontAwesomeIcon icon="circle-exclamation" /> Champs obligatoires non mappés : {missingReq.map(f => f.label).join(", ")}
        </div>
      )}

      <div className="flex items-center justify-between gap-4 flex-wrap">
        <button onClick={onBack} className="text-sm text-slate-500 hover:text-slate-700"><FontAwesomeIcon icon="arrow-left" /> Retour</button>
        <div className="flex items-center gap-3">
          <p className="text-xs text-slate-500 max-w-xs text-right hidden md:block">
            <FontAwesomeIcon icon="shield-halved" className="text-emerald-500" /> Le test est une <b>simulation sans risque</b> : rien n'est écrit. Il compte ce qui sera créé, mis à jour ou rejeté.
          </p>
          <select value={strategy} onChange={e => setStrategy(e.target.value)} className="h-10 px-2 rounded-md border border-slate-200 text-sm bg-white">
            {STRATEGIES.map(s => <option key={s.k} value={s.k}>{s.fr}</option>)}
          </select>
          <button onClick={onTest} disabled={busy || missingReq.length > 0} className="inline-flex items-center gap-1.5 h-10 px-4 rounded-lg bg-brand-500 hover:bg-brand-600 disabled:opacity-50 text-white text-sm font-medium">
            <FontAwesomeIcon icon={busy ? "spinner" : "flask"} spin={busy} /> Tester l'import (simulation)
          </button>
        </div>
      </div>
    </div>
  )
}

// ── STEP: Preview (dry-run + inline correction) ─────────────────────────────
const PreviewStep = ({ test, schema, mapping, busy, onBack, onCorrect, onLaunch }) => {
  const ok = test.processed || 0, errors = test.error_count || 0, total = test.total || 0
  const errorRows = test.errors || []
  const mappedKeys = new Set(Object.values(mapping).filter(Boolean))
  const cols = schema.filter(f => mappedKeys.has(f.key))
  const [edits, setEdits] = useState({})
  useEffect(() => { setEdits({}) }, [test])
  const val = (row, key) => (edits[row.row] && key in edits[row.row]) ? edits[row.row][key] : ((row.values && row.values[key]) || "")
  const setCell = (rn, key, v) => setEdits(e => ({ ...e, [rn]: { ...(e[rn] || {}), [key]: v } }))
  const submitFix = () => onCorrect(errorRows.map(r => { const values = {}; cols.forEach(c => values[c.key] = val(r, c.key)); return { row: r.row, values } }))

  const staging = test.staging && test.staging.counts
  const summaryBits = staging
    ? STAGING.filter(s => (staging[s.k] ?? 0) > 0).map(s => `${staging[s.k]} ${s.fr.toLowerCase()}`)
    : []
  return (
    <div className="space-y-4">
      {/* Result banner — makes clear the test is a completed, no-risk simulation */}
      <div className={`rounded-2xl border p-5 ${errors > 0 ? "bg-amber-50 border-amber-200" : "bg-emerald-50 border-emerald-200"}`}>
        <div className="flex items-start gap-3">
          <div className={`w-10 h-10 shrink-0 grid place-items-center rounded-full text-lg ${errors > 0 ? "bg-amber-100 text-amber-600" : "bg-emerald-100 text-emerald-600"}`}>
            <FontAwesomeIcon icon={errors > 0 ? "triangle-exclamation" : "circle-check"} />
          </div>
          <div className="min-w-0">
            <div className="font-semibold text-slate-900">
              <Bi fr="Simulation terminée — aucune donnée n'a été modifiée" en="Simulation complete — no data was changed" />
            </div>
            <p className="text-sm text-slate-600 mt-0.5">
              Sur <b>{total}</b> ligne(s) analysée(s), voici ce que l'import réel effectuera{summaryBits.length ? " : " : "."}
              {summaryBits.length > 0 && <b>{summaryBits.join(" · ")}.</b>}
              {" "}Vérifiez, corrigez si besoin, puis lancez.
            </p>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-3 gap-3">
        {[["Lignes analysées", total, "text-slate-900", "Total lues dans le fichier"],
          ["Prêtes à importer", ok, "text-emerald-600", "Passent la validation"],
          ["Bloquées", errors, errors ? "text-rose-600" : "text-slate-900", "À corriger avant import"]].map(([l, v, c, hint]) => (
          <div key={l} className="bg-white border border-slate-200 rounded-xl p-4 text-center">
            <div className={`text-2xl font-bold tabular-nums ${c}`}>{v}</div>
            <div className="text-xs font-medium text-slate-600 mt-1">{l}</div>
            <div className="text-[11px] text-slate-400 mt-0.5">{hint}</div>
          </div>
        ))}
      </div>

      {/* Per-row staging — what commit will do */}
      {staging && (
        <div className="bg-white border border-slate-200 rounded-2xl p-4">
          <div className="text-sm font-semibold mb-1"><Bi fr="Détail : ce qui sera appliqué à chaque ligne" en="Detail: what will be applied per row" /></div>
          <p className="text-xs text-slate-500 mb-3">Résultat du rapprochement de chaque ligne avec vos données existantes.</p>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {STAGING.map(s => (
              <div key={s.k} className={`rounded-xl border p-3 ${s.cls}`}>
                <div className="flex items-center gap-2 text-xs font-medium"><FontAwesomeIcon icon={s.icon} /> <Bi fr={s.fr} en={s.en} /></div>
                <div className="text-2xl font-bold tabular-nums mt-1">{staging[s.k] ?? 0}</div>
              </div>
            ))}
          </div>
          {(staging.ambiguous > 0) && (
            <p className="text-xs text-amber-700 mt-3"><FontAwesomeIcon icon="circle-info" /> Les lignes « à vérifier » n'ont pas d'identifiant : elles seront créées mais ne pourront pas être rapprochées d'un enregistrement existant.</p>
          )}
        </div>
      )}
      {(test.duplicates || []).length > 0 && (
        <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
          <div className="font-medium mb-1"><FontAwesomeIcon icon="clone" /> Doublons détectés dans le fichier</div>
          <ul className="list-disc list-inside">{test.duplicates.map((d, i) => <li key={i}>{d.value} ×{d.count}</li>)}</ul>
        </div>
      )}
      {errors > 0 && (
        <div className="bg-white border border-slate-200 rounded-2xl p-4">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-semibold">Corriger les lignes en erreur</h3>
            <button onClick={submitFix} disabled={busy} className="inline-flex items-center gap-1.5 h-8 px-3 rounded-md bg-brand-500 hover:bg-brand-600 disabled:opacity-50 text-white text-[12.5px] font-medium"><FontAwesomeIcon icon={busy ? "spinner" : "wand-magic-sparkles"} spin={busy} /> Corriger et re-tester</button>
          </div>
          <div className="overflow-x-auto border border-slate-200 rounded-lg max-h-96 overflow-y-auto">
            <table className="w-full text-[13px]">
              <thead className="sticky top-0 bg-slate-50"><tr className="text-slate-500 text-[11px] uppercase tracking-wider border-b border-slate-200">
                <th className="text-left font-medium px-3 py-2 w-12">L.</th>
                {cols.map(c => <th key={c.key} className="text-left font-medium px-3 py-2 whitespace-nowrap">{c.label}</th>)}
                <th className="text-left font-medium px-3 py-2">Erreur</th>
              </tr></thead>
              <tbody className="divide-y divide-slate-100">
                {errorRows.map(r => (
                  <tr key={r.row}>
                    <td className="px-3 py-1.5 tabular-nums text-slate-400">{r.row}</td>
                    {cols.map(c => <td key={c.key} className="px-1.5 py-1.5"><input value={val(r, c.key)} onChange={e => setCell(r.row, c.key, e.target.value)} className="w-full h-8 px-2 rounded border border-slate-200 text-sm focus:border-brand-400 focus:outline-none" /></td>)}
                    <td className="px-3 py-1.5 text-rose-600 text-xs whitespace-pre-line">{r.message}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <a href={test.correction_url} className="inline-block mt-2.5 text-sm text-brand-600 hover:text-brand-700 font-medium"><FontAwesomeIcon icon="download" /> Télécharger les erreurs</a>
        </div>
      )}
      <div className="flex items-center justify-end gap-2">
        <button onClick={onBack} className="text-sm text-slate-500 hover:text-slate-700 px-3"><FontAwesomeIcon icon="arrow-left" /> Retour</button>
        <button onClick={onLaunch} disabled={busy || ok === 0} className="inline-flex items-center gap-1.5 h-10 px-5 rounded-lg bg-brand-500 hover:bg-brand-600 disabled:opacity-50 text-white text-sm font-medium"><FontAwesomeIcon icon="rocket" /> Lancer l'import ({ok})</button>
      </div>
    </div>
  )
}

// ── STEP: Report ─────────────────────────────────────────────────────────
const ReportStep = ({ result, target, urls, onNew, onHistory, onOpenDetail }) => {
  const cancelled = result.status === "cancelled"
  const [reverting, setRev] = useState(false)
  const [reverted, setReverted] = useState(false)
  const revert = () => {
    if (!window.confirm("Annuler cet import et supprimer les enregistrements créés ? Cette action est réversible tant que les objets n'ont pas de dépendances.")) return
    setRev(true)
    postRequest({ url: urls.revert_batch, body: { import_ids: [result.id] } })
      .then(r => { setReverted((r.reverted || []).includes(result.id)); setRev(false) })
      .catch(() => setRev(false))
  }
  const hasErrors = (result.rejected_count ?? result.error_count) > 0
  return (
    <div className="space-y-4">
      <div className="bg-white border border-slate-200 rounded-2xl p-8 text-center">
        <div className={`w-16 h-16 mx-auto grid place-items-center rounded-full text-2xl mb-4 ${cancelled ? "bg-slate-100 text-slate-500" : reverted ? "bg-slate-100 text-slate-500" : "bg-emerald-50 text-emerald-600"}`}>
          <FontAwesomeIcon icon={cancelled ? "ban" : reverted ? "rotate-left" : "circle-check"} />
        </div>
        <h2 className="text-2xl font-bold">
          {reverted ? <Bi fr="Import annulé" en="Import reverted" /> : cancelled ? <Bi fr="Import arrêté" en="Import stopped" /> : <Bi fr="Import terminé" en="Import complete" />}
        </h2>
        <p className="text-slate-600 mt-2">
          {result.created_count ?? 0} créé(s) · {result.updated_count ?? 0} mis à jour
          {(result.warning_count ?? 0) > 0 ? ` · ${result.warning_count} warning(s)` : ""}
          {hasErrors ? ` · ${result.rejected_count ?? result.error_count} rejeté(s)` : ""}
          {` sur ${result.total} ligne(s).`}
        </p>
        <div className="flex items-center justify-center gap-2 mt-6 flex-wrap">
          <button onClick={onNew} className="inline-flex items-center gap-1.5 h-10 px-4 rounded-lg border border-slate-200 text-slate-700 text-sm font-medium hover:bg-slate-50"><FontAwesomeIcon icon="plus" /> Nouvel import</button>
          <button onClick={onHistory} className="inline-flex items-center gap-1.5 h-10 px-4 rounded-lg border border-slate-200 text-slate-700 text-sm font-medium hover:bg-slate-50"><FontAwesomeIcon icon="clock-rotate-left" /> Historique</button>
          {!reverted && urls?.revert_batch && (result.rollbackable || cancelled) && (
            <button onClick={revert} disabled={reverting} className="inline-flex items-center gap-1.5 h-10 px-4 rounded-lg border border-rose-200 text-rose-600 text-sm font-medium hover:bg-rose-50 disabled:opacity-50">
              <FontAwesomeIcon icon={reverting ? "spinner" : "rotate-left"} spin={reverting} /> {cancelled ? "Nettoyer les enregistrements créés" : "Annuler l'import"}
            </button>
          )}
          <button onClick={() => onOpenDetail && onOpenDetail(result.id)} className="inline-flex items-center gap-1.5 h-10 px-4 rounded-lg bg-brand-500 hover:bg-brand-600 text-white text-sm font-medium"><FontAwesomeIcon icon="arrow-right" /> Voir le bilan</button>
        </div>
      </div>
      {(result.errors?.length > 0 || result.warnings?.length > 0) && (
        <LogConsole errors={result.errors} warnings={result.warnings} errorTotal={result.rejected_count ?? result.error_count} warningTotal={result.warning_count} compact />
      )}
    </div>
  )
}

// ── MULTI-FILE CHAIN ─────────────────────────────────────────────────────
// Drop N files → each is ingested & auto-detected → sorted by object
// importance (so referenced objects import first and relations reconnect via
// codes) → imported in cascade with per-file progress.
const JOB_STATUS = {
  detecting: { fr: "Analyse…",   cls: "bg-slate-100 text-slate-600", icon: "spinner", spin: true },
  ready:     { fr: "Prêt",       cls: "bg-blue-50 text-blue-700",    icon: "circle-check" },
  error:     { fr: "Erreur",     cls: "bg-rose-50 text-rose-700",    icon: "triangle-exclamation" },
  waiting:   { fr: "En file",    cls: "bg-slate-100 text-slate-500", icon: "hourglass-half" },
  running:   { fr: "Import…",    cls: "bg-amber-50 text-amber-700",  icon: "spinner", spin: true },
  done:      { fr: "Terminé",    cls: "bg-emerald-50 text-emerald-700", icon: "circle-check" },
  cancelled: { fr: "Arrêté",     cls: "bg-slate-100 text-slate-600", icon: "ban" },
  reverted:  { fr: "Annulé",     cls: "bg-slate-100 text-slate-500", icon: "rotate-left" },
  failed:    { fr: "Échec",      cls: "bg-rose-50 text-rose-700",    icon: "circle-xmark" },
}

const MultiChain = ({ catalog, urls, strategy, setStrategy, onExit, onSeeHistory }) => {
  const inputRef = useRef(null)
  const [drag, setDrag] = useState(false)
  const [jobs, setJobs] = useState([]) // {uid, filename, status, type, name, confidence, importId, suggestion, total, error, live}
  const [running, setRunning] = useState(false)
  const [reverting, setReverting] = useState(false)
  const [revertReport, setRevertReport] = useState(null)
  const uidRef = useRef(0)
  const byKey = key => catalog.find(o => o.key === key) || null
  const rankOf = key => { const o = byKey(key); return o ? o.rank : 999 }
  const importUrl = id => `${urls.base}/${id}`
  const patch = (uid, fields) => setJobs(js => js.map(j => j.uid === uid ? { ...j, ...fields } : j))

  // Ingest a file, letting the backend auto-detect (type "auto") or forcing a
  // chosen type on override.
  const ingestFile = (uid, file, type) => {
    patch(uid, { status: "detecting", error: null })
    const form = new FormData()
    form.append("type_of_import", type || "auto")
    form.append("mode", strategy)
    form.append("data_file", file)
    formRequest({ url: urls.create, form })
      .then(p => {
        if (p.error) { patch(uid, { status: "error", error: p.error }); return }
        const top = (p.detection || [])[0] || {}
        patch(uid, {
          status: "ready", importId: p.id, type: p.type, name: byKey(p.type)?.name || p.type,
          confidence: top.confidence, suggestion: p.suggestion, total: p.total, live: null,
        })
      })
      .catch(() => patch(uid, { status: "error", error: "Erreur réseau" }))
  }

  const addFiles = fileList => {
    Array.from(fileList || []).forEach(file => {
      const uid = ++uidRef.current
      setJobs(js => [...js, { uid, filename: file.name, status: "detecting", file }])
      ingestFile(uid, file, "auto")
    })
  }

  // Override the detected object: re-ingest the same file as the chosen type.
  const retype = (uid, typeKey) => {
    const job = jobs.find(j => j.uid === uid)
    if (job?.file) ingestFile(uid, job.file, typeKey)
    else patch(uid, { type: typeKey, name: byKey(typeKey)?.name || typeKey })
  }

  const removeJob = uid => setJobs(js => js.filter(j => j.uid !== uid))

  // Poll an import until it leaves a busy state, patching the job's live view.
  const waitTerminal = async (job, delay) => {
    let cur = await getRequest(importUrl(job.importId))
    patch(job.uid, { live: cur })
    while (BUSY_STATUSES.includes(cur.status)) {
      await new Promise(r => setTimeout(r, delay))
      cur = await getRequest(importUrl(job.importId))
      patch(job.uid, { live: cur })
    }
    return cur
  }

  // Dry-run (async) → wait until tested → commit → wait until done. The commit
  // endpoint requires status `tested`, so we MUST let the simulation finish
  // first (otherwise authorize :update? rejects a still-testing import).
  const runOne = async job => {
    patch(job.uid, { status: "running", live: null })
    try {
      const cols = {}
      Object.entries(job.suggestion || {}).forEach(([h, f]) => { if (f) cols[h] = f })

      // 1) launch the non-blocking dry-run, then wait for it to reach `tested`.
      const mapRes = await patchRequest({ url: importUrl(job.importId), body: { columns: cols, mode: strategy } })
      if (mapRes.valid === false) { patch(job.uid, { status: "failed", error: (mapRes.errors || []).join(" · ") }); return }
      patch(job.uid, { live: mapRes })
      const tested = await waitTerminal(job, 500)
      if (tested.status !== "tested") {
        patch(job.uid, { status: tested.status === "cancelled" ? "cancelled" : "failed", live: tested, error: tested.status === "cancelled" ? "Arrêté" : "Échec de la simulation" })
        return
      }

      // 2) commit for real, then wait until done.
      const committed = await postRequest({ url: `${importUrl(job.importId)}/commit` })
      if (committed.error) { patch(job.uid, { status: "failed", error: committed.error }); return }
      patch(job.uid, { live: committed })
      const final = await waitTerminal(job, 700)
      patch(job.uid, { status: final.status === "cancelled" ? "cancelled" : "done", live: final })
    } catch (e) {
      patch(job.uid, { status: "failed", error: "Erreur réseau" })
    }
  }

  // Run in WAVES by dependency rank: files of the same object run in parallel,
  // and a wave only starts once the previous one is done — so referenced
  // objects (clients, sites…) exist before the files that point at them.
  const runChain = async () => {
    setRunning(true); setRevertReport(null)
    const runnable = jobs.filter(j => j.status === "ready")
    const byRank = {}
    runnable.forEach(j => { (byRank[rankOf(j.type)] ||= []).push(j) })
    runnable.forEach(j => patch(j.uid, { status: "waiting" }))
    for (const rank of Object.keys(byRank).map(Number).sort((a, b) => a - b)) {
      await Promise.all(byRank[rank].map(runOne))
    }
    setRunning(false)
  }

  const stopJob = job => { if (job.live?.cancel_url) postRequest({ url: job.live.cancel_url }).catch(() => {}) }

  // One-click reversible recovery: revert every committed file in reverse
  // dependency order.
  const revertAll = () => {
    const ids = jobs.filter(j => j.importId && ["done", "cancelled", "failed"].includes(j.status)).map(j => j.importId)
    if (!ids.length) return
    if (!window.confirm(`Annuler toute la reprise (${ids.length} fichier(s)) et supprimer les enregistrements créés ?`)) return
    setReverting(true)
    postRequest({ url: urls.revert_batch, body: { import_ids: ids } })
      .then(r => {
        setRevertReport(r)
        setJobs(js => js.map(j => (r.reverted || []).includes(j.importId) ? { ...j, status: "reverted" } : j))
      })
      .finally(() => setReverting(false))
  }

  const ordered   = [...jobs].sort((a, b) => rankOf(a.type) - rankOf(b.type))
  const readyCount = jobs.filter(j => j.status === "ready").length
  const detecting  = jobs.some(j => j.status === "detecting")
  const committed  = jobs.filter(j => j.importId && ["done", "cancelled"].includes(j.status))
  const allDone    = jobs.length > 0 && jobs.every(j => ["done", "failed", "error", "cancelled", "reverted"].includes(j.status))
  const ranks      = [...new Set(ordered.filter(j => j.type).map(j => rankOf(j.type)))].sort((a, b) => a - b)

  return (
    <div className="space-y-5">
      <div>
        <h2 className="text-xl font-semibold"><Bi fr="Reprise de données multi-fichiers" en="Multi-file data recovery" /></h2>
        <p className="text-slate-500 mt-1"><Bi fr="Déposez plusieurs fichiers : Yuman détecte chaque objet, importe en parallèle par vagues de dépendances, et vous pouvez tout annuler en un clic." en="Drop several files: Yuman detects each object, imports in parallel by dependency waves, and you can revert everything in one click." /></p>
      </div>

      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <span className="text-sm text-slate-500">Stratégie</span>
          <select value={strategy} onChange={e => setStrategy(e.target.value)} disabled={running} className="h-9 px-2 rounded-md border border-slate-200 text-sm bg-white disabled:opacity-50">
            {STRATEGIES.map(s => <option key={s.k} value={s.k}>{s.fr}</option>)}
          </select>
        </div>
        <button onClick={onExit} className="text-sm text-slate-500 hover:text-slate-700"><FontAwesomeIcon icon="arrow-left" /> Retour</button>
      </div>

      {!running && !committed.length && (
        <div onClick={() => inputRef.current?.click()}
             onDragOver={e => { e.preventDefault(); setDrag(true) }} onDragLeave={() => setDrag(false)}
             onDrop={e => { e.preventDefault(); setDrag(false); addFiles(e.dataTransfer.files) }}
             className={`cursor-pointer rounded-2xl border-2 border-dashed px-6 py-10 text-center transition ${drag ? "border-brand-400 bg-brand-50" : "border-slate-300 hover:border-slate-400 bg-slate-50"}`}>
          <div className="text-3xl text-slate-400 mb-2"><FontAwesomeIcon icon="layer-group" /></div>
          <div className="font-medium text-slate-800"><Bi fr="Déposez plusieurs fichiers CSV / Excel" en="Drop several CSV / Excel files" /></div>
          <div className="text-xs text-slate-400 mt-1 font-mono">Détection, tri par dépendances & import parallèle</div>
          <input ref={inputRef} type="file" multiple accept=".csv,.xlsx,.xlsm,text/csv" className="hidden" onChange={e => addFiles(e.target.files)} />
        </div>
      )}

      {revertReport && (
        <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4 text-sm">
          <div className="font-semibold text-slate-800 mb-1"><FontAwesomeIcon icon="rotate-left" /> Reprise annulée</div>
          <p className="text-slate-600">{(revertReport.reverted || []).length} import(s) annulé(s){(revertReport.blocked || []).length ? `, ${revertReport.blocked.length} bloqué(s)` : ""}{(revertReport.skipped || []).length ? `, ${revertReport.skipped.length} ignoré(s)` : ""}.</p>
          {(revertReport.blocked || []).length > 0 && (
            <ul className="mt-2 list-disc list-inside text-rose-600 text-xs">
              {revertReport.blocked.map((b, i) => <li key={i}>#{b.id} ({b.object}) — {b.reason}</li>)}
            </ul>
          )}
        </div>
      )}

      {jobs.length > 0 && (
        <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden">
          <div className="px-5 py-3 border-b border-slate-200 flex items-center justify-between gap-3 flex-wrap">
            <div className="text-sm font-semibold">{jobs.length} fichier(s){ranks.length > 1 ? ` · ${ranks.length} vagues de dépendances` : ""}</div>
            <div className="flex items-center gap-2">
              {committed.length > 0 && (
                <button onClick={revertAll} disabled={reverting || running}
                        className="inline-flex items-center gap-1.5 h-9 px-4 rounded-lg border border-rose-200 text-rose-600 text-sm font-medium hover:bg-rose-50 disabled:opacity-50">
                  <FontAwesomeIcon icon={reverting ? "spinner" : "rotate-left"} spin={reverting} /> Tout annuler
                </button>
              )}
              {allDone
                ? <button onClick={onSeeHistory} className="inline-flex items-center gap-1.5 h-9 px-4 rounded-lg border border-slate-200 text-slate-700 text-sm font-medium hover:bg-slate-50"><FontAwesomeIcon icon="clock-rotate-left" /> Historique</button>
                : <button onClick={runChain} disabled={running || detecting || readyCount === 0}
                          className="inline-flex items-center gap-1.5 h-9 px-4 rounded-lg bg-brand-500 hover:bg-brand-600 disabled:opacity-50 text-white text-sm font-medium">
                    <FontAwesomeIcon icon={running ? "spinner" : "play"} spin={running} /> Lancer la reprise ({readyCount})
                  </button>}
            </div>
          </div>
          <ul className="divide-y divide-slate-100">
            {ordered.map(j => {
              const st = JOB_STATUS[j.status] || JOB_STATUS.detecting
              const wave = j.type ? ranks.indexOf(rankOf(j.type)) + 1 : null
              const live = j.live
              const pct = live?.percentage || 0
              return (
                <li key={j.uid} className="px-5 py-3">
                  <div className="flex items-center gap-3">
                    {wave ? <span className="w-6 h-6 shrink-0 grid place-items-center rounded-full bg-slate-100 text-slate-500 text-[11px] font-semibold tabular-nums" title={`Vague ${wave}`}>{wave}</span>
                          : <FontAwesomeIcon icon="file-csv" className="text-slate-400 w-6 text-center" />}
                    <div className="min-w-0 flex-1">
                      <div className="text-sm font-medium truncate">{j.filename}</div>
                      <div className="text-xs text-slate-400">
                        {j.type ? <>{j.name}{j.confidence != null ? ` · ${j.confidence}%` : ""}{j.total != null ? ` · ${j.total} lignes` : ""}</> : "—"}
                        {j.error ? <span className="text-rose-500"> · {j.error}</span> : null}
                      </div>
                    </div>
                    {j.status === "ready" && (
                      <select value={j.type} onChange={e => retype(j.uid, e.target.value)} className="h-8 px-2 rounded-md border border-slate-200 text-xs bg-white max-w-[150px]">
                        {catalog.map(o => <option key={o.key} value={o.key}>{o.name}</option>)}
                      </select>
                    )}
                    {j.status === "running" && j.live?.cancel_url && (
                      <button onClick={() => stopJob(j)} title="Arrêter" className="inline-grid place-items-center w-8 h-8 rounded-md border border-rose-200 text-rose-600 hover:bg-rose-50"><FontAwesomeIcon icon="stop" /></button>
                    )}
                    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${st.cls}`}>
                      <FontAwesomeIcon icon={st.icon} spin={st.spin} /> {st.fr}
                    </span>
                    {(j.status === "ready" || j.status === "error") && !running && (
                      <button onClick={() => removeJob(j.uid)} className="text-slate-300 hover:text-rose-500"><FontAwesomeIcon icon="xmark" /></button>
                    )}
                  </div>
                  {live && ["running", "done", "cancelled"].includes(j.status) && (
                    <div className="mt-2 pl-9">
                      <div className="h-1.5 rounded-full bg-slate-200 overflow-hidden">
                        <div className={`h-full transition-all ${j.status === "done" ? "bg-emerald-500" : j.status === "cancelled" ? "bg-slate-400" : "bg-brand-500"}`} style={{ width: `${pct}%` }} />
                      </div>
                      <div className="flex gap-3 mt-1 text-[11px] text-slate-400 tabular-nums">
                        <span>{live.processed}/{live.total}</span>
                        <span className="text-emerald-600">+{live.created_count ?? 0}</span>
                        <span className="text-blue-600">~{live.updated_count ?? 0}</span>
                        {(live.rejected_count ?? live.error_count) > 0 && <span className="text-rose-600">✗{live.rejected_count ?? live.error_count}</span>}
                        {(live.warning_count ?? 0) > 0 && <span className="text-amber-600">⚠{live.warning_count}</span>}
                      </div>
                    </div>
                  )}
                </li>
              )
            })}
          </ul>
        </div>
      )}
    </div>
  )
}

// ── IMPORT DETAIL (rich report for a history entry) ─────────────────────────
// Errors / warnings journal with the row values + reason, and a link to the
// downloadable error file so the user can fix their source.
const DetailLogs = ({ errors = [], warnings = [], rejected, warningTotal, correctionUrl }) => {
  const [tab, setTab] = useState(rejected > 0 ? "errors" : "warnings")
  const rows = tab === "errors" ? errors : warnings
  return (
    <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden">
      <div className="flex items-center gap-2 px-5 pt-4 flex-wrap">
        {[["errors", "Erreurs", rejected, "bg-rose-50 text-rose-700 ring-1 ring-rose-200", "circle-xmark"],
          ["warnings", "Warnings", warningTotal, "bg-amber-50 text-amber-700 ring-1 ring-amber-200", "triangle-exclamation"]].map(([k, label, n, active, ic]) => (
          <button key={k} onClick={() => setTab(k)} className={`inline-flex items-center gap-2 px-3 h-9 rounded-lg text-sm font-medium transition ${tab === k ? active : "text-slate-500 hover:bg-slate-50"}`}>
            <FontAwesomeIcon icon={ic} /> {label} <span className="tabular-nums text-[11px] px-1.5 py-0.5 rounded-full bg-slate-100 text-slate-600">{n || 0}</span>
          </button>
        ))}
        {tab === "errors" && rejected > 0 && correctionUrl && (
          <a href={correctionUrl} className="ml-auto inline-flex items-center gap-1.5 h-9 px-3 rounded-lg border border-rose-200 text-rose-600 text-sm font-medium hover:bg-rose-50">
            <FontAwesomeIcon icon="download" /> Fichier d'erreurs (CSV)
          </a>
        )}
      </div>
      <div className="mt-3 border-t border-slate-100 overflow-x-auto max-h-[28rem] overflow-y-auto">
        {rows.length === 0 ? (
          <div className="py-10 text-center text-slate-400 text-sm"><FontAwesomeIcon icon="circle-check" className="text-xl mb-2 block" /> Aucune entrée</div>
        ) : (
          <table className="w-full text-[13px]">
            <thead className="sticky top-0 bg-white"><tr className="text-slate-500 text-[11px] uppercase tracking-wider border-b border-slate-100">
              <th className="text-left font-medium px-4 py-2 w-14">Ligne</th>
              <th className="text-left font-medium px-4 py-2">Valeurs de la ligne</th>
              <th className="text-left font-medium px-4 py-2 w-2/5">Raison</th>
            </tr></thead>
            <tbody className="divide-y divide-slate-50">
              {rows.map((r, i) => (
                <tr key={i} className="align-top hover:bg-slate-50/60">
                  <td className="px-4 py-2 tabular-nums text-slate-400">{r.row ?? "—"}</td>
                  <td className="px-4 py-2">
                    <div className="flex flex-wrap gap-1">
                      {Object.entries(r.values || {}).filter(([, v]) => v != null && v !== "").slice(0, 8).map(([f, v]) => (
                        <span key={f} className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-slate-50 border border-slate-200 text-[11px]">
                          <span className="text-slate-400">{f}</span><span className="text-slate-700 font-medium truncate max-w-[140px]">{String(v)}</span>
                        </span>
                      ))}
                      {Object.keys(r.values || {}).length === 0 && <span className="text-slate-300">—</span>}
                    </div>
                  </td>
                  <td className={`px-4 py-2 whitespace-pre-line ${tab === "errors" ? "text-rose-600" : "text-amber-700"}`}>{r.message}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  )
}

const ImportDetail = ({ id, urls, onBack, onChanged }) => {
  const [d, setD] = useState(null)
  const [busy, setBusy] = useState(false)
  useEffect(() => {
    let alive = true
    getRequest(`${urls.base}/${id}/detail`).then(r => alive && setD(r)).catch(() => alive && setD({ error: true }))
    return () => { alive = false }
  }, [id])

  const back = <button onClick={onBack} className="text-sm text-slate-500 hover:text-slate-700"><FontAwesomeIcon icon="arrow-left" /> Historique</button>

  if (!d) return <div className="bg-white border border-slate-200 rounded-2xl p-16 text-center text-slate-400"><FontAwesomeIcon icon="spinner" spin className="text-2xl" /></div>
  if (d.error) return (
    <div className="bg-white border border-slate-200 rounded-2xl p-14 text-center text-slate-500">
      <FontAwesomeIcon icon="triangle-exclamation" className="text-2xl mb-2 block text-rose-400" /> Import introuvable.
      <div className="mt-4">{back}</div>
    </div>
  )

  const st = STATUS_STYLE[d.status] || { cls: "bg-slate-100 text-slate-600", fr: d.status }
  const rejected = d.rejected_count ?? d.error_count ?? 0
  const fmtDate = iso => { try { return new Date(iso).toLocaleString("fr-FR", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }) } catch (e) { return "—" } }
  const fmtDur = s => !s ? "instantané" : s < 60 ? `${Math.round(s)} s` : `${Math.floor(s / 60)} min ${Math.round(s % 60)} s`
  const revert = () => {
    if (!window.confirm("Annuler cet import et supprimer les enregistrements créés ?")) return
    setBusy(true)
    postRequest({ url: urls.revert_batch, body: { import_ids: [id] } }).then(() => onChanged()).finally(() => setBusy(false))
  }
  const del = () => {
    if (!window.confirm("Supprimer cette entrée de l'historique ?\n\nLes données déjà importées sont CONSERVÉES.")) return
    setBusy(true)
    deleteRequest(`${urls.base}/${id}`).then(() => onChanged()).finally(() => setBusy(false))
  }
  const btn = "inline-flex items-center gap-1.5 h-9 px-3 rounded-lg border text-sm font-medium"

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        {back}
        <div className="flex items-center gap-2 flex-wrap">
          {d.file_url && <a href={d.file_url} className={`${btn} border-slate-200 text-slate-600 hover:bg-slate-50`}><FontAwesomeIcon icon="download" /> Fichier source</a>}
          {rejected > 0 && d.correction_url && <a href={d.correction_url} className={`${btn} border-rose-200 text-rose-600 hover:bg-rose-50`}><FontAwesomeIcon icon="file-csv" /> Fichier d'erreurs</a>}
          {d.rollbackable && <button onClick={revert} disabled={busy} className={`${btn} border-rose-200 text-rose-600 hover:bg-rose-50 disabled:opacity-50`}><FontAwesomeIcon icon={busy ? "spinner" : "rotate-left"} spin={busy} /> Annuler l'import</button>}
          {d.deletable !== false && <button onClick={del} disabled={busy} title="Supprimer l'entrée (conserve les données)" className={`${btn} border-slate-200 text-slate-500 hover:bg-rose-50 hover:text-rose-600`}><FontAwesomeIcon icon="trash-can" /></button>}
        </div>
      </div>

      <div className="bg-white border border-slate-200 rounded-2xl p-6">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <div className="text-xs text-slate-400 uppercase tracking-wide">{d.object} · {d.mode}</div>
            <h2 className="text-xl font-bold mt-0.5 break-all">{d.filename}</h2>
            <div className="text-xs text-slate-500 mt-1">Créé le {fmtDate(d.created_at)} · durée {fmtDur(d.duration)}</div>
          </div>
          <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-sm font-medium ${st.cls}`}><span className="w-1.5 h-1.5 rounded-full bg-current opacity-70" />{st.fr}</span>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-6 gap-3 mt-5">
          {[["Lignes", d.total, "text-slate-900"],
            ["Traités", d.processed, "text-slate-900"],
            ["Créés", d.created_count, "text-emerald-600"],
            ["Mis à jour", d.updated_count, "text-blue-600"],
            ["Rejetés", rejected, rejected ? "text-rose-600" : "text-slate-400"],
            ["Warnings", d.warning_count, d.warning_count ? "text-amber-600" : "text-slate-400"]].map(([l, v, c]) => (
            <div key={l} className="rounded-xl border border-slate-200 p-3">
              <div className={`text-2xl font-bold tabular-nums ${c}`}>{v ?? 0}</div>
              <div className="text-[11px] text-slate-500 mt-0.5">{l}</div>
            </div>
          ))}
        </div>
      </div>

      {d.related && d.related.count > 0 && (
        <div className="bg-white border border-slate-200 rounded-2xl p-5 flex items-center justify-between gap-4 flex-wrap">
          <div>
            <div className="font-semibold text-slate-900"><FontAwesomeIcon icon="database" className="text-brand-500" /> {d.related.count} enregistrement(s) rattaché(s)</div>
            <p className="text-sm text-slate-500 mt-0.5">Les {String(d.object).toLowerCase()} créés ou mis à jour par cet import.</p>
          </div>
          {d.related.url && <a href={d.related.url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 h-10 px-4 rounded-lg bg-brand-500 hover:bg-brand-600 text-white text-sm font-medium"><FontAwesomeIcon icon="arrow-up-right-from-square" /> Voir les enregistrements</a>}
        </div>
      )}

      {(rejected > 0 || (d.warning_count ?? 0) > 0) && (
        <DetailLogs errors={d.errors} warnings={d.warnings} rejected={rejected} warningTotal={d.warning_count} correctionUrl={d.correction_url} />
      )}

      {Object.keys(d.columns || {}).length > 0 && (
        <div className="bg-white border border-slate-200 rounded-2xl p-5">
          <div className="text-sm font-semibold mb-3"><Bi fr="Correspondance des colonnes" en="Column mapping" /></div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-0.5 text-[13px]">
            {Object.entries(d.columns).map(([csv, field]) => (
              <div key={csv} className="flex items-center gap-2 py-1.5 border-b border-slate-50">
                <span className="font-mono text-slate-600 truncate flex-1">{csv}</span>
                <FontAwesomeIcon icon="arrow-right" className="text-slate-300 text-xs shrink-0" />
                <span className={`flex-1 truncate ${field ? "text-slate-700" : "text-slate-300 italic"}`}>{field || "ignoré"}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}

// ── HISTORY ────────────────────────────────────────────────────────────────
const History = ({ urls, onOpen }) => {
  const [rows, setRows] = useState(null)
  const [busyId, setBusyId] = useState(null)
  const load = () => getRequest(urls.history).then(setRows).catch(() => setRows([]))
  useEffect(() => { load() }, [])

  // Revert through the batch endpoint (works without a Sidekiq worker), then
  // refresh the list.
  const revertRow = id => {
    if (!window.confirm("Annuler cet import et supprimer les enregistrements créés ?")) return
    setBusyId(id)
    postRequest({ url: urls.revert_batch, body: { import_ids: [id] } })
      .then(() => load())
      .finally(() => setBusyId(null))
  }

  // Delete a history entry: removes the import record + its logs only. The
  // imported data is KEPT (import_id nullified) — this is not a revert.
  const deleteRow = id => {
    if (!window.confirm("Supprimer cette entrée de l'historique ?\n\nLes données déjà importées sont CONSERVÉES ; seul le suivi de cet import (et la possibilité de l'annuler) est retiré.")) return
    setBusyId(id)
    deleteRequest(`${urls.base}/${id}`)
      .then(() => load())
      .finally(() => setBusyId(null))
  }

  return (
    <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden">
      <div className="px-6 py-4 border-b border-slate-200">
        <h2 className="text-lg font-semibold"><Bi fr="Historique des imports" en="Import History" /></h2>
        <p className="text-sm text-slate-500"><Bi fr="Consultez et gérez vos imports passés" en="View and manage past imports" /></p>
      </div>
      {rows === null ? (
        <div className="py-16 text-center text-slate-400"><FontAwesomeIcon icon="spinner" spin /></div>
      ) : rows.length === 0 ? (
        <div className="py-16 text-center text-slate-500"><FontAwesomeIcon icon="inbox" className="text-2xl mb-2 block" /> Aucun import.</div>
      ) : (
        <table className="w-full table-fixed text-[13px]">
          <colgroup>
            <col className="w-[112px]" /><col /><col className="w-[132px]" /><col className="w-[68px]" />
            <col className="w-[104px]" /><col className="w-[60px]" /><col className="w-[60px]" /><col className="w-[88px]" />
          </colgroup>
          <thead><tr className="text-slate-500 text-[11px] uppercase tracking-wider border-b border-slate-200">
            <th className="text-left font-medium px-3 py-3">Date</th>
            <th className="text-left font-medium px-3 py-3">Fichier</th>
            <th className="text-left font-medium px-3 py-3">Objet</th>
            <th className="text-left font-medium px-3 py-3">Mode</th>
            <th className="text-left font-medium px-3 py-3">Statut</th>
            <th className="text-right font-medium px-3 py-3" title="Lignes importées">Import.</th>
            <th className="text-right font-medium px-3 py-3" title="Lignes échouées">Échecs</th>
            <th className="text-center font-medium px-3 py-3">Actions</th>
          </tr></thead>
          <tbody className="divide-y divide-slate-100">
            {rows.map(r => {
              const st = STATUS_STYLE[r.status] || { cls: "bg-slate-100 text-slate-600", fr: r.status }
              return (
                <tr key={r.id} onClick={() => onOpen && onOpen(r.id)} className="hover:bg-brand-50/40 cursor-pointer transition">
                  <td className="px-3 py-3 text-slate-500 whitespace-nowrap tabular-nums text-xs">{new Date(r.date).toLocaleString("fr-FR", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })}</td>
                  <td className="px-3 py-3 font-mono text-xs truncate text-brand-700" title={r.filename}>{r.filename}</td>
                  <td className="px-3 py-3"><span className="inline-block max-w-full truncate align-middle px-2 py-0.5 rounded-full bg-slate-100 text-slate-600 text-xs" title={r.object}>{r.object}</span></td>
                  <td className="px-3 py-3 text-slate-600 capitalize text-xs truncate">{r.mode}</td>
                  <td className="px-3 py-3"><span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${st.cls}`}><span className="w-1.5 h-1.5 rounded-full bg-current opacity-70" />{st.fr}</span></td>
                  <td className="px-3 py-3 text-right tabular-nums text-emerald-600 font-medium">{r.imported}</td>
                  <td className="px-3 py-3 text-right tabular-nums font-medium">{r.failed > 0 ? <span className="text-rose-600">{r.failed}</span> : <span className="text-slate-300">0</span>}</td>
                  <td className="px-3 py-3" onClick={e => e.stopPropagation()}>
                    <div className="flex items-center justify-center gap-1">
                      {r.rollbackable
                        ? <button onClick={() => revertRow(r.id)} disabled={busyId === r.id} title="Annuler cet import (supprime les enregistrements créés)" className="inline-grid place-items-center w-8 h-8 rounded-md border border-slate-200 text-slate-600 hover:bg-slate-50 hover:text-rose-600 disabled:opacity-50"><FontAwesomeIcon icon={busyId === r.id ? "spinner" : "rotate-left"} spin={busyId === r.id} /></button>
                        : <span title="Non annulable" className="inline-grid place-items-center w-8 h-8 rounded-md text-slate-300"><FontAwesomeIcon icon="ban" /></span>}
                      {r.deletable === false
                        ? <span title="Import en cours — non supprimable" className="inline-grid place-items-center w-8 h-8 rounded-md text-slate-200"><FontAwesomeIcon icon="trash-can" /></span>
                        : <button onClick={() => deleteRow(r.id)} disabled={busyId === r.id} title="Supprimer l'entrée (conserve les données importées)" className="inline-grid place-items-center w-8 h-8 rounded-md border border-slate-200 text-slate-400 hover:bg-rose-50 hover:text-rose-600 hover:border-rose-200 disabled:opacity-50"><FontAwesomeIcon icon={busyId === r.id ? "spinner" : "trash-can"} spin={busyId === r.id} /></button>}
                    </div>
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      )}
    </div>
  )
}

export default App
